import { Seo } from '../components/Seo';
import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { ExperienceCard } from '../components/OfferCards';
import { AffiliateDisclosure } from '../components/AffiliateDisclosure';
import { FilteredResults } from '../components/filters/FilteredResults';
import { DiscoveryCollection, DestinationTiles } from '../components/DiscoverySections';
import { SCHEMAS } from '../lib/filterSchemas';
import { LocationSearch } from '../components/LocationSearch';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { api } from '../lib/api';
import { useApp } from '../context/AppContext';
import { openDeal } from '../lib/deals';
import { usePersistentState, clearPersisted } from '../lib/usePersistentState';
import { Ticket, Search, MapPin, ArrowLeft } from 'lucide-react';

const CATEGORIES = ['Adventure', 'Food', 'Culture', 'Nightlife', 'Tours', 'Theme Parks', 'Museums', 'Water Sports', 'Shopping', 'Nature', 'Family', 'Romantic', 'Wellness'];

export default function Experiences() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { currency } = useApp();
  const [dest, setDest] = usePersistentState('exp:dest', null);
  const [travellers, setTravellers] = usePersistentState('exp:travellers', 1);
  const [keyword, setKeyword] = usePersistentState('exp:keyword', params.get('keyword') || '');
  const [date, setDate] = usePersistentState('exp:date', params.get('date') || '');
  const [category, setCategory] = usePersistentState('exp:category', params.get('category') || '');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pageNum, setPageNum] = usePersistentState('exp:pageNum', 1);
  const [hasMore, setHasMore] = usePersistentState('exp:hasMore', false);
  const [data, setData] = usePersistentState('exp:data', null);
  const [discovery, setDiscovery] = useState(null);

  const search = useCallback(async (code, cat, kw) => {
    setLoading(true); setPageNum(1);
    try {
      const { data: res } = await api.experiences({
        destination_code: code, destination: dest?.name || dest?.city || '',
        travellers, category: cat ?? category, keyword: kw ?? keyword, date, currency, page: 1,
      });
      setHasMore(!!res.has_more);
      setData(res);
    } catch (e) { console.error('Experience search failed', e); } finally { setLoading(false); }
  }, [dest, travellers, category, keyword, date, currency]);

  const loadMore = useCallback(async () => {
    if (!data || loadingMore) return;
    const next = pageNum + 1;
    setLoadingMore(true);
    try {
      const { data: res } = await api.experiences({
        destination_code: dest?.code || '', destination: dest?.name || dest?.city || '',
        travellers, category, keyword, date, currency, page: next,
      });
      setPageNum(next);
      setHasMore(!!res.has_more);
      setData((prev) => (prev ? { ...res, offers: [...prev.offers, ...(res.offers || [])] } : res));
    } catch (e) { console.error('Load more failed', e); } finally { setLoadingMore(false); }
  }, [data, loadingMore, pageNum, dest, travellers, category, keyword, date, currency]);

  useEffect(() => {
    api.discoveryExperiences().then(({ data }) => setDiscovery(data)).catch((e) => console.error('Failed to load experience discovery', e));
    const code = params.get('destination_code');
    // Skip auto-run when a restored search already exists (returning from a detail page).
    if (code && !data) { setDest({ code, city: code, name: code }); search(code, params.get('category') || ''); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.get('destination_code')]);

  const pickCategory = (c) => { const next = c === category ? '' : c; setCategory(next); if (dest) search(dest.code, next); };

  // Viator experiences open a native detail page; demo/mock offers open the partner directly.
  const onViewOffer = (offer) => {
    if (offer.product_code) {
      const q = new URLSearchParams({
        city: offer.city || dest?.name || dest?.city || '',
        ...(offer.price_inr ? { price: String(offer.price_inr) } : {}),
        ...(offer.duration_hours ? { dur: String(offer.duration_hours) } : {}),
      }).toString();
      navigate(`/experiences/${offer.product_code}?${q}`);
      return;
    }
    openDeal({ type: 'experience', offer, surface: 'experience_results', source: '/experiences' });
  };

  // A live experience card opens its native detail page; a destination tile runs a search.
  const selectItem = (d) => {
    if (d.product_code) {
      const q = new URLSearchParams({
        city: d.destination || '',
        ...(d.price ? { price: String(d.price) } : {}),
        ...(d.duration_hours ? { dur: String(d.duration_hours) } : {}),
      }).toString();
      navigate(`/experiences/${d.product_code}?${q}`);
      return;
    }
    const code = d.search_code || d.destination_code || d.code;
    const name = d.name || d.destination;
    const cat = d.category && CATEGORIES.includes(d.category) ? d.category : category;
    setDest({ code, city: name, name });
    if (cat !== category) setCategory(cat);
    setParams((p) => { p.set('destination_code', code); if (cat) p.set('category', cat); return p; }, { replace: true });
    search(code, cat);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const clearSearch = () => { setData(null); setDest(null); clearPersisted('exp:'); setParams({}, { replace: true }); };

  return (
    <Layout>
      <Seo title="Experiences, Tours & Activities" path="/experiences"
        description="Discover tours, activities and things to do worldwide on FlightDeals.in. Compare experiences by price, rating and duration and book with trusted partners." />
      <section className="bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="font-display text-3xl font-semibold flex items-center gap-2"><Ticket className="h-7 w-7 text-accent" /> Experiences</h1>
          <p className="text-primary-foreground/75 mt-1">Tours, food, adventure, culture & more — booked with trusted partners.</p>
          <div className="mt-5 rounded-2xl bg-card text-foreground p-4 grid gap-3 grid-cols-1 md:grid-cols-2 lg:grid-cols-12 items-end">
            <div className="min-w-0 md:col-span-2 lg:col-span-4"><LocationSearch vertical="experience" label="Destination" placeholder="City, destination or attraction" value={dest} onChange={setDest} testid="experience-destination-input" icon={MapPin} /></div>
            <div className="min-w-0 md:col-span-2 lg:col-span-3">
              <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Keyword</Label>
              <Input value={keyword} onChange={(e) => setKeyword(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && dest) search(dest.code, category, keyword); }} placeholder="e.g. food tour, Eiffel Tower" className="h-12 w-full min-w-0" data-testid="experience-keyword-input" />
            </div>
            <div className="min-w-0 lg:col-span-2">
              <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Travel date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-12 w-full min-w-0 max-w-full box-border [&::-webkit-calendar-picker-indicator]:shrink-0 [&::-webkit-datetime-edit]:min-w-0 [&::-webkit-datetime-edit]:overflow-hidden" data-testid="experience-date-input" />
            </div>
            <div className="min-w-0 lg:col-span-1"><Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Guests</Label><Input type="number" min={1} value={travellers} onChange={(e) => setTravellers(e.target.value)} className="h-12 w-full min-w-0" data-testid="experience-travellers" /></div>
            <div className="min-w-0 md:col-span-2 lg:col-span-2"><Button onClick={() => (dest || keyword) && search(dest?.code || '', category, keyword)} disabled={!dest && !keyword} className="w-full h-12 rounded-xl bg-accent text-accent-foreground hover:bg-accent/90 font-semibold" data-testid="experience-search-button"><Search className="h-4 w-4 mr-1.5" /> Search</Button></div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2 mb-4" data-testid="experience-categories">
          {CATEGORIES.map((c) => (
            <button key={c} onClick={() => pickCategory(c)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap border transition-colors ${category === c ? 'bg-accent/12 text-accent border-accent/30' : 'bg-card text-muted-foreground border-border hover:bg-secondary'}`}
              data-testid={`category-${c.toLowerCase().replace(' ', '-')}`}>{c}</button>
          ))}
        </div>
        <div className="mb-4"><AffiliateDisclosure variant="banner" /></div>

        {loading && <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{[1, 2, 3, 4, 5, 6].map((i) => <Skeleton key={i} className="h-72 rounded-2xl" />)}</div>}

        {!loading && data && (
          <>
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-muted-foreground" data-testid="experience-results-summary">
                {data.total ? `${data.total.toLocaleString('en-IN')} experiences` : `${data.count} experiences`}
                {(data.destination?.city || keyword) ? ` for ${keyword || data.destination.city}` : ''}
                {data.is_mock && <span className="ml-2 text-xs bg-secondary px-2 py-0.5 rounded" data-testid="experience-demo-label">demo data</span>}
              </p>
              <Button variant="ghost" size="sm" onClick={clearSearch} className="text-xs" data-testid="experience-back-to-discovery"><ArrowLeft className="h-3.5 w-3.5 mr-1" /> Discover more</Button>
            </div>
            {data.count === 0 ? (
              <div className="rounded-2xl border border-border bg-card p-10 text-center" data-testid="experience-empty">
                <Ticket className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                <h3 className="font-semibold">No experiences found</h3>
                <p className="text-sm text-muted-foreground mt-1">Try a different destination or keyword.</p>
              </div>
            ) : (
              <FilteredResults schema={SCHEMAS.experience} items={data.offers} testId="experience-results" countNoun="experience"
                gridClassName="grid gap-4 sm:grid-cols-2"
                renderItem={(o) => <ExperienceCard key={o.offer_id} offer={o} currency={currency} onViewDeal={onViewOffer} />} />
            )}
            {hasMore && data.count > 0 && (
              <div className="flex justify-center mt-6">
                <Button variant="outline" onClick={loadMore} disabled={loadingMore} className="rounded-xl px-8" data-testid="experience-load-more">
                  {loadingMore ? 'Loading…' : 'Load more experiences'}
                </Button>
              </div>
            )}
          </>
        )}

        {!loading && !data && (
          <div data-testid="experience-discovery">
            {!discovery && <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{[1, 2, 3, 4, 5, 6].map((i) => <Skeleton key={i} className="h-56 rounded-2xl" />)}</div>}
            {discovery && (
              <>
                {discovery.is_demo && (
                  <p className="text-xs text-muted-foreground mb-4 rounded-lg bg-secondary/60 px-3 py-2 inline-block" data-testid="experience-discovery-demo-note">
                    Live experiences are powered by Viator — pick a destination above to see real availability & prices. The trending cards below are illustrative previews.
                  </p>
                )}
                {discovery.destinations?.length > 0 && (
                  <DestinationTiles destinations={discovery.destinations} onSelect={selectItem} title="Top destinations" />
                )}
                {(discovery.collections || []).map((c) => (
                  <DiscoveryCollection key={c.key} collection={c} currency={currency} onSelect={selectItem} />
                ))}
              </>
            )}
          </div>
        )}
      </section>
    </Layout>
  );
}
